/**
 * The P1Plug status vocabulary: one set of words for every interface.
 *
 * Each state has a definition and a tone; the label is what people read.
 * Tones follow the design system (status-and-errors): success = verified
 * good, warning = delayed or needs attention soon, critical = loss of
 * service or data, info = neutral progress, unknown = not yet seen.
 *
 * The backend's own enumerations (device.status, inventory_state, order
 * status …) are mapped onto these in gateways/live/mappers.ts and never
 * shown as they are. "Active" is deliberately absent: it could mean
 * activated, connected, assigned or sending data.
 */
export type Tone = 'success' | 'warning' | 'critical' | 'info' | 'unknown';

export interface StateDefinition {
  label: string;
  /** Dutch label, used by the consumer dialect. */
  labelNl: string;
  tone: Tone;
  definition: string;
}

const define = <K extends string>(states: Record<K, StateDefinition>) => states;

/** Where a device is in its life. One value per device. */
export const DEVICE_LIFECYCLE = define({
  provisioning: {
    label: 'Provisioning',
    labelNl: 'Wordt ingericht',
    tone: 'info',
    definition: 'Manufactured; identity, certificate or tests not yet complete.',
  },
  ready: {
    label: 'Ready',
    labelNl: 'Gereed',
    tone: 'unknown',
    definition: 'Provisioned and in stock; not bound to an order.',
  },
  assigned: {
    label: 'Assigned',
    labelNl: 'Toegewezen',
    tone: 'info',
    definition: 'Bound to an order and an installation; being packed.',
  },
  connecting: {
    label: 'Connecting',
    labelNl: 'Verbinding maken',
    tone: 'info',
    definition: 'Shipped or delivered; has not yet activated itself.',
  },
  online: {
    label: 'Online',
    labelNl: 'Verbonden',
    tone: 'success',
    definition: 'Activated and seen within its online threshold.',
  },
  offline: {
    label: 'Offline',
    labelNl: 'Geen verbinding',
    tone: 'critical',
    definition: 'Activated but not seen within its online threshold.',
  },
  attention: {
    label: 'Attention',
    labelNl: 'Aandacht nodig',
    tone: 'warning',
    definition:
      'Connected, but something about it needs an operator: missing data, resets, a configuration that does not apply.',
  },
  retired: {
    label: 'Retired',
    labelNl: 'Uit gebruik',
    tone: 'unknown',
    definition: 'Returned, replaced, blocked or scrapped; no longer delivers data.',
  },
});
export type DeviceLifecycle = keyof typeof DEVICE_LIFECYCLE;

/** Whether meter data is arriving. Separate from connectivity on purpose. */
export const DATA_STATE = define({
  current: {
    label: 'Data current',
    labelNl: 'Energiedata is actueel',
    tone: 'success',
    definition: 'The latest reading is within three reporting intervals.',
  },
  delayed: {
    label: 'Data delayed',
    labelNl: 'Data loopt achter',
    tone: 'warning',
    definition: 'Readings arrived before, but the latest is older than three reporting intervals.',
  },
  none: {
    label: 'No data yet',
    labelNl: 'Nog geen data',
    tone: 'unknown',
    definition: 'No reading has ever been received.',
  },
});
export type DataState = keyof typeof DATA_STATE;

export const ORDER_STATE = define({
  received: {
    label: 'Received',
    labelNl: 'Ontvangen',
    tone: 'info',
    definition: 'Accepted from the customer; not yet validated.',
  },
  ready_for_fulfilment: {
    label: 'Ready for fulfilment',
    labelNl: 'Klaar voor verzending',
    tone: 'info',
    definition: 'Validated; waiting for a device to be picked.',
  },
  processing: {
    label: 'Processing',
    labelNl: 'In behandeling',
    tone: 'info',
    definition: 'A device is assigned and the parcel is being prepared, or a replacement is under way.',
  },
  shipped: { label: 'Shipped', labelNl: 'Onderweg', tone: 'info', definition: 'Handed to the carrier.' },
  delivered: {
    label: 'Delivered',
    labelNl: 'Bezorgd',
    tone: 'info',
    definition: 'Delivered; the device has not connected yet.',
  },
  activated: {
    label: 'Activated',
    labelNl: 'Geactiveerd',
    tone: 'success',
    definition: 'The device connected and bound itself to the installation.',
  },
  cancelled: {
    label: 'Cancelled',
    labelNl: 'Geannuleerd',
    tone: 'unknown',
    definition: 'Cancelled before activation.',
  },
  exception: {
    label: 'Exception',
    labelNl: 'Uitzondering',
    tone: 'critical',
    definition: 'Cannot continue without an operator.',
  },
});
export type OrderState = keyof typeof ORDER_STATE;

export const MANUFACTURING_STATE = define({
  pending: { label: 'Pending', labelNl: 'Wacht', tone: 'unknown', definition: 'Built; not yet on a test station.' },
  testing: { label: 'Testing', labelNl: 'Wordt getest', tone: 'info', definition: 'On a test station.' },
  passed: {
    label: 'Passed',
    labelNl: 'Goedgekeurd',
    tone: 'success',
    definition: 'All stages passed and QA approved; may enter stock.',
  },
  failed: {
    label: 'Failed',
    labelNl: 'Afgekeurd',
    tone: 'critical',
    definition: 'At least one stage failed; may be retested or quarantined.',
  },
  quarantined: {
    label: 'Quarantined',
    labelNl: 'In quarantaine',
    tone: 'warning',
    definition: 'Held back for inspection; cannot be shipped.',
  },
});
export type ManufacturingState = keyof typeof MANUFACTURING_STATE;

export const SERVICE_STATE = define({
  operational: { label: 'Operational', labelNl: 'Operationeel', tone: 'success', definition: 'Working as intended.' },
  degraded: {
    label: 'Degraded performance',
    labelNl: 'Verminderde prestaties',
    tone: 'warning',
    definition: 'Working, but slower or with delays.',
  },
  partial_outage: {
    label: 'Partial outage',
    labelNl: 'Gedeeltelijke storing',
    tone: 'critical',
    definition: 'Unavailable for some customers or functions.',
  },
  major_outage: { label: 'Major outage', labelNl: 'Grote storing', tone: 'critical', definition: 'Unavailable.' },
  maintenance: { label: 'Maintenance', labelNl: 'Onderhoud', tone: 'info', definition: 'Planned work in progress.' },
  not_monitored: {
    label: 'Not monitored yet',
    labelNl: 'Nog niet gemeten',
    tone: 'unknown',
    definition: 'No measurement behind this component yet; it says nothing about the service.',
  },
});
export type ServiceState = keyof typeof SERVICE_STATE;

export const SEVERITY = define({
  critical: {
    label: 'Critical',
    labelNl: 'Kritiek',
    tone: 'critical',
    definition: 'A customer is without service or data now.',
  },
  warning: {
    label: 'Warning',
    labelNl: 'Waarschuwing',
    tone: 'warning',
    definition: 'Will affect a customer if nobody acts.',
  },
  info: { label: 'Notice', labelNl: 'Melding', tone: 'info', definition: 'Worth knowing; no customer impact.' },
});
export type Severity = keyof typeof SEVERITY;
